import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, } from 'react';
import { Box } from '@mui/material';
import { loadEnvConfig } from '../../../services/envConfig';
import { isEmbedUrlAllowed } from '../../../utils/permitDetailsEmbed';
import { preconnectOrigin } from '../../../utils/preconnect';
import { PageLoadScreen } from '../../library/loading/loading';
const FlowViewerHostContext = createContext(null);
export function useFlowViewerHost() {
    return useContext(FlowViewerHostContext);
}
/** Offscreen geometry — mounted and booted, but invisible and inert. */
const HIDDEN_RECT = { top: 0, left: -99999, width: 1, height: 1 };
/**
 * The loading cover hides the frame from the moment a new permit is requested until the Flow Viewer signals
 * it has rendered (`postMessage('flowViewerReady')`) — so the PREVIOUS permit is never shown while the new one
 * loads. This fallback lifts the cover if no ready message arrives; keep it comfortably long, since lifting
 * before the new permit has painted would briefly reveal the old one.
 */
const COVER_FALLBACK_MS = 1200;
/**
 * Owns a single Flow Viewer iframe for the whole session:
 *  - boots it once (warm connection + cached bundle) when the CMS is idle,
 *  - lets a page `present()` it over a slot and navigate it by route,
 *  - keeps it alive on `release()` so the next open reuses the same booted app.
 */
export const FlowViewerHostProvider = ({ children }) => {
    const [origin, setOrigin] = useState('');
    /** Current URL loaded in the iframe: the warm root, then permit routes (same origin+path, hash differs). */
    const [route, setRoute] = useState('');
    const [presented, setPresented] = useState(false);
    const [rect, setRect] = useState(HIDDEN_RECT);
    /** True while a newly-requested permit is loading — the skeleton covers the frame so the previous permit
     *  is never shown. Cleared when the Flow Viewer signals ready (or the fallback elapses). */
    const [pending, setPending] = useState(false);
    const targetRef = useRef(null);
    /** Last route the iframe was pointed at — lets present() tell a real permit change from a re-present. */
    const routeRef = useRef('');
    // Resolve the Flow Viewer origin, warm the connection, and boot the iframe at APP START (Option 2):
    // download the bundle + boot the app in the hidden/offscreen iframe right after the CMS's first paint, so
    // it is already running well before the first permit is opened. A tiny defer avoids contending with the
    // initial CMS render.
    useEffect(() => {
        let active = true;
        let timer = null;
        void loadEnvConfig().then((env) => {
            if (!active || !env)
                return;
            const raw = env.ProcessFlowDetailsUrl ?? env.PermitViewDetailsUrl;
            if (typeof raw !== 'string' || !raw.trim())
                return;
            let resolved;
            try {
                const u = new URL(raw, window.location.href);
                if (u.protocol !== 'http:' && u.protocol !== 'https:')
                    return;
                if (u.origin === window.location.origin)
                    return;
                resolved = u.origin;
            }
            catch {
                return;
            }
            preconnectOrigin(resolved);
            timer = window.setTimeout(() => {
                if (active)
                    setOrigin(resolved); // iframe src → downloads bundle + boots app (hidden/offscreen)
            }, 120);
        });
        return () => {
            active = false;
            if (timer != null)
                window.clearTimeout(timer);
        };
    }, []);
    // Warm boot: load the origin root once, only while nothing else has been requested.
    useEffect(() => {
        if (origin && !route)
            setRoute(`${origin}/`);
    }, [origin, route]);
    // Lift the cover after the fallback window if no ready signal arrives. Keyed to `route` so each new permit
    // restarts the timer. Prefer the postMessage signal below — a too-short fallback would flash the old permit.
    useEffect(() => {
        if (!pending)
            return;
        const t = window.setTimeout(() => setPending(false), COVER_FALLBACK_MS);
        return () => window.clearTimeout(t);
    }, [pending, route]);
    // Accurate lift: the Flow Viewer posts 'flowViewerReady' once it has rendered the requested permit.
    // Harmless if it never sends one — the fallback timer above still lifts the cover.
    useEffect(() => {
        if (!origin)
            return;
        const onMessage = (e) => {
            if (e.origin !== origin)
                return;
            const d = e.data;
            const ready = d === 'flowViewerReady' ||
                (typeof d === 'object' && d !== null && d.type === 'flowViewerReady');
            if (ready)
                setPending(false);
        };
        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, [origin]);
    const syncRect = useCallback(() => {
        const el = targetRef.current;
        if (!el)
            return;
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    }, []);
    const present = useCallback((target, nextRoute) => {
        if (nextRoute && !isEmbedUrlAllowed(nextRoute)) {
            console.warn('[embed] Refused to load a URL outside the embed allow-list.');
            return;
        }
        targetRef.current = target;
        if (nextRoute && nextRoute !== routeRef.current) {
            // A different permit: cover the frame with the skeleton FIRST (so the previous permit is never shown),
            // then point the warm iframe at the new route. The cover lifts when the Flow Viewer signals ready.
            routeRef.current = nextRoute;
            setPending(true);
            setRoute(nextRoute);
        }
        setPresented(true);
        const r = target.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    }, []);
    const release = useCallback(() => {
        targetRef.current = null;
        setPresented(false);
        // Keep the last rect so the frame fades out in place (parking it offscreen would snap/flash).
        // While not presented it is opacity 0 + pointer-events none, so it is invisible and click-through.
        // Route is kept so the app stays booted; the next present() just changes the fragment.
    }, []);
    const prefetch = useCallback((nextRoute) => {
        if (!nextRoute || nextRoute === routeRef.current || !isEmbedUrlAllowed(nextRoute))
            return;
        // Point the (still hidden) warm iframe at the permit now, so the Flow Viewer starts fetching its data
        // before the user clicks. `pending` is set so that when present() shows this same route, the cover
        // stays until the permit is ready (or clears early via 'flowViewerReady' during the prefetch window).
        routeRef.current = nextRoute;
        setPending(true);
        setRoute(nextRoute);
    }, []);
    /** Stable context value — a fresh object each render would make consumers' effects thrash present/release. */
    const value = useMemo(() => ({ present, release, prefetch }), [present, release, prefetch]);
    // Keep the iframe aligned to its slot while presented (layout shifts, navbar height, window resize).
    useEffect(() => {
        if (!presented)
            return;
        syncRect();
        const el = targetRef.current;
        const ro = el ? new ResizeObserver(() => syncRect()) : null;
        if (el && ro)
            ro.observe(el);
        const onWin = () => syncRect();
        window.addEventListener('resize', onWin);
        window.addEventListener('scroll', onWin, true);
        return () => {
            ro?.disconnect();
            window.removeEventListener('resize', onWin);
            window.removeEventListener('scroll', onWin, true);
        };
    }, [presented, syncRect]);
    return (_jsxs(FlowViewerHostContext.Provider, { value: value, children: [children, route ? (_jsxs("div", { "aria-hidden": !presented, style: {
                    position: 'fixed',
                    top: rect.top,
                    left: rect.left,
                    width: rect.width,
                    height: rect.height,
                    opacity: presented ? 1 : 0,
                    /* Gentle cross-fade on show/hide — keeps navigation instant but avoids the hard flash. */
                    transition: 'opacity 240ms ease',
                    /* Invisible + click-through when not presented, so it never blocks the page underneath. */
                    pointerEvents: presented ? 'auto' : 'none',
                    zIndex: presented ? 2 : 1,
                    overflow: 'hidden',
                }, children: [_jsx("iframe", { title: "Permit details", src: route, style: { width: '100%', height: '100%', border: 'none', display: 'block' } }), _jsx(Box, { "aria-hidden": true, sx: {
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'background.default',
                            opacity: presented && pending ? 1 : 0,
                            pointerEvents: presented && pending ? 'auto' : 'none',
                            transition: pending ? 'none' : 'opacity 220ms ease',
                            zIndex: 1,
                        }, children: _jsx(PageLoadScreen, { variant: "embedded", transparent: true }) })] })) : null] }));
};
